import {
  AugmentedCreatePresentationBundleInput,
  EntityKind,
  PresentationBundleKind,
  PresentationPageKind,
} from '@/types/schema';

import { TRUST_ENTITY_TYPES } from '../../entities.constants';
import { EntityType } from '../../types/EntityType';
import { getEntityTypeFromEntityKind } from '../../utils/getEntityTypeFromEntityKind';

export function makeGenerateEntityNotesFromDocumentSummaryInput({
  documentSummaryId,
  entityId,
  householdId,
  activeDeathOrderId,
  isTimeBoundEntity,
  syncDeathOrderNotes,
}: {
  documentSummaryId: string;
  entityId: string;
  householdId: string;
  isTimeBoundEntity: boolean;
  activeDeathOrderId: string | null | undefined;
  syncDeathOrderNotes: boolean;
}) {
  return {
    documentSummaryID: documentSummaryId,
    entityID: entityId,
    // time bound entities don't have a client profile on their notes
    // if sync notes, then use the entity note with nil ClientProfile
    clientProfileIDs:
      isTimeBoundEntity || syncDeathOrderNotes || !activeDeathOrderId
        ? []
        : [activeDeathOrderId],
    householdID: householdId,
  };
}

// same list as BUSINESS_ENTITY_TYPES without sole-proprietorship
export const SUPPORTED_AI_GENERATED_NOTE_BUSINESS_ENTITY_KINDS: Readonly<
  EntityType[]
> = ['c-corp', 's-corp', 'llc', 'lp', 'gp'] as const;
export function isEntityNotesEnabled(entityKind: EntityKind) {
  return (
    TRUST_ENTITY_TYPES.includes(getEntityTypeFromEntityKind(entityKind)) ||
    SUPPORTED_AI_GENERATED_NOTE_BUSINESS_ENTITY_KINDS.includes(
      getEntityTypeFromEntityKind(entityKind)
    )
  );
}

/**
 * Builds the single-page custom-page bundle that carries an entity's client
 * presentation notes: the entity's display name becomes the page name and the
 * notes become the page content.
 */
export function makeAddNotesCustomBundleInput({
  householdId,
  entityDisplayName,
  notes,
}: {
  householdId: string;
  entityDisplayName: string;
  notes: string;
}): AugmentedCreatePresentationBundleInput {
  return {
    create: {
      householdID: householdId,
      kind: PresentationBundleKind.CustomPageBundle,
    },
    withCustomPageBundleConfiguration: {
      create: {
        householdID: householdId,
        includePageHeader: false,
      },
    },
    withPages: [
      {
        create: {
          householdID: householdId,
          kind: PresentationPageKind.CustomPage,
          index: 0,
        },
        withCustomPage: {
          create: {
            householdID: householdId,
            pageName: entityDisplayName,
            pageContent: notes,
          },
        },
      },
    ],
  };
}

/**
 * Picks the custom-page bundle to navigate to after appending notes. Appending
 * without an index places the new custom bundle last, and a single-page custom
 * bundle uses its bundle id as the designer's selection id.
 */
export function selectAppendedCustomBundle<
  T extends { kind: PresentationBundleKind },
>(bundles: T[]): T | undefined {
  return bundles
    .filter((bundle) => bundle.kind === PresentationBundleKind.CustomPageBundle)
    .at(-1);
}
